import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type Language = 'he' | 'en'

const LANGUAGE_KEY = 'poker-pot-live.language.v1'
let activeLanguage: Language = 'en'

function browserDefault(): Language {
  if (typeof navigator === 'undefined') return 'en'
  return navigator.language.toLowerCase().startsWith('he') ? 'he' : 'en'
}

function initialLanguage(): Language {
  if (typeof localStorage === 'undefined') return browserDefault()
  const saved = localStorage.getItem(LANGUAGE_KEY)
  return saved === 'he' || saved === 'en' ? saved : browserDefault()
}

export function selectText(language: Language, english: string, hebrew: string): string {
  return language === 'he' ? hebrew : english
}

export function text(english: string, hebrew: string): string {
  return selectText(activeLanguage, english, hebrew)
}

export function pickBilingual(value: string): string {
  const divider = ' · '
  if (!value.includes(divider)) return value
  const parts = value.split(divider)
  const boundary = Math.ceil(parts.length / 2)
  return activeLanguage === 'he'
    ? parts.slice(boundary).join(divider)
    : parts.slice(0, boundary).join(divider)
}

interface LanguageContextValue {
  language: Language
  setLanguage: (language: Language) => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(initialLanguage)
  activeLanguage = language

  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, language)
    document.documentElement.lang = language
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr'
    document.title = text('Poker Pot Live', 'Poker Pot Live — קופה חיה')
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      text('Live chips and pot for home poker', 'קופה וצ׳יפים חיים לפוקר ביתי'),
    )
  }, [language])

  const value = useMemo(() => ({ language, setLanguage }), [language])
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage(): LanguageContextValue {
  const value = useContext(LanguageContext)
  if (!value) throw new Error('LanguageProvider is missing')
  return value
}

export function LanguageToggle() {
  const { language, setLanguage } = useLanguage()
  return (
    <div className="language-toggle" role="group" aria-label={text('Choose language', 'בחירת שפה')}>
      <button type="button" className={language === 'he' ? 'active' : ''} aria-pressed={language === 'he'} onClick={() => setLanguage('he')}>עברית</button>
      <button type="button" className={language === 'en' ? 'active' : ''} aria-pressed={language === 'en'} onClick={() => setLanguage('en')}>EN</button>
    </div>
  )
}
